import React, { useEffect, useState, useRef, useMemo } from "react";
import style from "./TaskMentorView.module.css";
import { baseurl } from "../Url";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBookOpen, FaUpload, FaFileAlt, FaVideo, FaImage, FaPencilAlt } from "react-icons/fa";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Loader from "../Loder/Loader";
import { IoChevronBack } from "react-icons/io5";
import LearningBottomNav from "../LearningBottomNav/LearningBottomNav";
import { useLearning } from "../Context/LearningContext";

const formatUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${baseurl}/${url}`;
};

const FILE_EXT_IN_URL =
  /\.(pdf|doc|docx|jpg|jpeg|png|gif|webp|txt|ppt|pptx|xls|xlsx)(\?|#|$)/i;

function isNotAnsweredText(answer) {
  if (answer == null) return true;
  const s = String(answer).trim();
  return s === "" || s.toLowerCase() === "not answered";
}

function submissionAnswerIsReviewableFileUrl(answer) {
  if (isNotAnsweredText(answer) || typeof answer !== "string") return false;
  const s = answer.trim();
  if (/^https?:\/\//i.test(s)) {
    const path = s.split("?")[0].split("#")[0];
    return FILE_EXT_IN_URL.test(path) || /amazonaws\.com|cloudfront\.net|blob\.core\.windows\.net/i.test(s);
  }
  if (s.startsWith("/") && FILE_EXT_IN_URL.test(s)) return true;
  return false;
}

function getSubmissionAnswerHref(answer) {
  if (!answer || typeof answer !== "string") return "";
  return formatUrl(answer.trim());
}

function displayNameFromSubmissionUrl(answer) {
  if (!answer || typeof answer !== "string") return "Submitted file";
  try {
    const path = answer.trim().split("?")[0].split("#")[0];
    const seg = path.split("/").filter(Boolean).pop() || "file";
    const decoded = decodeURIComponent(seg);
    const underscore = decoded.lastIndexOf("_");
    if (underscore > 0 && underscore < decoded.length - 1) {
      return decoded.slice(underscore + 1);
    }
    return decoded;
  } catch {
    return "Submitted file";
  }
}

function submissionAnswerIsImageUrl(answer) {
  if (!submissionAnswerIsReviewableFileUrl(answer)) return false;
  const path = getSubmissionAnswerHref(answer).split("?")[0].toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp)(\?|#|$)/i.test(path);
}

function isSubmissionEvaluated(task) {
  const v = task?.evaluated;
  return v === true || v === "true" || v === 1;
}

function TaskMentorView() {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useLearning();
  const learningData = context?.learningData || {};

  const [activeTaskIndex, setActiveTaskIndex] = useState(0);
  const [activeContentIndex, setActiveContentIndex] = useState(0);
  const [activityDetails, setActivityDetails] = useState([]);
  const key = JSON.parse(localStorage.getItem("access-token"));
  const USERID = localStorage.getItem("USERID");
  const viewUserId = location.state?.viewUserId;
  const effectiveUserId = viewUserId || USERID;
  const OrgID = localStorage.getItem("ORGID");
  const main_topic_id = localStorage.getItem("main_topic_id");
  const UserName = localStorage.getItem("username");
  const [activityComments, setActivityComments] = useState({});
  const [aiID, setAiID] = useState(null);
  const [file, setFile] = useState(null);
  const [singleTopic, setSingleTopic] = useState(null);
  const [learningID, setLearningID] = useState(null);
  const [uniqueId, setUniqueId] = useState(null);
  const [topicNo, setTopicNo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roomID, setRoomID] = useState(false);
  const [comment, setComment] = useState(false);
  const [mappedLessons, setMappedLessons] = useState(null);
  const [assessmentExist, setAssessmentExist] = useState(false);
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [mainTopicID, setMainTopicID] = useState(null);
  const [showTaskContent, setShowTaskContent] = useState(false);
  const [taskDocId, setTaskDocId] = useState(null);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [submissionData, setSubmissionData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isTopicAlreadyComplete, setIsTopicAlreadyComplete] = useState(false);
  const [expandedMainTopics, setExpandedMainTopics] = useState({});
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [enteredMarks, setEnteredMarks] = useState({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [mentorLoading, setMentorLoading] = useState(false);
  const [categoryID, setCategoryID] = useState(null);
  /** When API returns evaluated:true, marks form is hidden until mentor clicks Update mark */
  const [marksEditorOpenByKey, setMarksEditorOpenByKey] = useState({});
  /** Paginate mentor view by task title group (one groupTaskTitle per page) */
  const [mentorTaskPageIndex, setMentorTaskPageIndex] = useState(0);

  const commentDivRef = useRef(null);
  const mentorViewTopRef = useRef(null);
  const skipMentorScrollOnMountRef = useRef(true);

  const handleMarksChange = (key, value) => {
    setEnteredMarks(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleMainTopic = (mainId) => {
    setExpandedMainTopics(prev => ({
      ...prev,
      [mainId]: !prev[mainId]
    }));
  };

  async function checkTopicStatus() {
    if (!effectiveUserId || !singleTopic?.id || !learningID) return;
    try {
      const resp = await fetch(`${baseurl}/userlearning/details/individual`, {
        method: "POST",
        body: JSON.stringify({
          userlearning_id: learningID,
          user_id: effectiveUserId
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await resp.json();
      if (data.status === true && data.data?.[0]?.Topics) {
        let foundComplete = false;
        for (const module of data.data[0].Topics) {
          const topic = module.topics.find(t => t.id === singleTopic.id);
          if (topic && topic.status === "complete") {
            foundComplete = true;
            break;
          }
        }
        setIsTopicAlreadyComplete(foundComplete);
      }
    } catch (error) {
      console.error("Error checking topic status:", error);
    }
  }

  useEffect(() => {
    if (singleTopic && learningID) {
      checkTopicStatus();
    }
  }, [singleTopic, learningID, effectiveUserId]);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMarksEditorOpenByKey({});
    setMentorTaskPageIndex(0);
    skipMentorScrollOnMountRef.current = true;
  }, [selectedTopic?.topic_id]);

  const mentorTaskGroups = useMemo(() => {
    if (!selectedTopic?.tasks?.length) return [];
    return Object.values(
      selectedTopic.tasks.reduce((acc, task) => {
        if (!acc[task.task_title]) acc[task.task_title] = [];
        acc[task.task_title].push(task);
        return acc;
      }, {})
    );
  }, [selectedTopic?.tasks]);

  useEffect(() => {
    if (
      mentorTaskGroups.length > 0 &&
      mentorTaskPageIndex >= mentorTaskGroups.length
    ) {
      setMentorTaskPageIndex(0);
    }
  }, [mentorTaskGroups.length, mentorTaskPageIndex]);

  useEffect(() => {
    if (skipMentorScrollOnMountRef.current) {
      skipMentorScrollOnMountRef.current = false;
      return;
    }
    mentorViewTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [mentorTaskPageIndex, selectedTopic?.topic_id]);

  const toggleMobileView = () => setShowTaskContent(!showTaskContent);

  const commentEditorChange = (event, editor, activityId) => {
    const data = editor.getData();
    setActivityComments((prevComments) => ({
      ...prevComments,
      [activityId]: data,
    }));
  };

  useEffect(() => {
    if (comment && commentDivRef.current) {
      commentDivRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [comment]);

  async function activityGet() {
    if (!singleTopic || !singleTopic.id) return;
    setLoading(true);
    try {
      const response = await fetch(`${baseurl}/task/list/all`, {
        method: "POST",
        body: JSON.stringify({
          org_id: OrgID,
          topic_id: singleTopic.id,
          ai_id: aiID,
          user_id: effectiveUserId,
          main_topic_id: mainTopicID,
          learning_id: learningID,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
      });
      const data = await response.json();
      setActivityDetails(data.data.tasks || []);
      if (data.data.id) {
        setTaskDocId(data.data.id);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching activity data:", error);
      setActivityDetails([]);
      setLoading(false);
    }
  }
  async function fetchMentorSubmissions() {
    if (!effectiveUserId || !mainTopicID) return;
    setMentorLoading(true);
    const resolvedCategoryId = categoryID ?? location.state?.category_id;
    try {
      const response = await fetch(`${baseurl}/task/submission/view/mentor`, {
        method: "POST",
        body: JSON.stringify({
          org_id: OrgID,
          user_id: effectiveUserId,
          ...(resolvedCategoryId != null &&
          resolvedCategoryId !== ""
            ? { category_id: resolvedCategoryId }
            : {}),
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
      });

      const data = await response.json();

      if (data.status === true) {
        const updated = data.data || [];
        setUserSubmissions(updated);
        const firstMain = updated[0];
        const firstTopic = firstMain?.topics?.[0] || null;
        const flatTopics = updated.flatMap((main) => main.topics || []);

        // Keep current selected topic if still present; else select first available topic.
        if (selectedTopic?.topic_id) {
          const latest = flatTopics.find((t) => t.topic_id === selectedTopic.topic_id);
          setSelectedTopic(latest || firstTopic);
        } else {
          setSelectedTopic(firstTopic);
        }

        // Expand first main topic by default when data exists.
        if (firstMain?.main_topic_id) {
          setExpandedMainTopics((prev) => ({
            ...prev,
            [firstMain.main_topic_id]: true,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching mentor submissions:", error);
    }
    finally {
      setMentorLoading(false);
    }
  }


  async function fetchSubmissions() {
    if (!effectiveUserId || !taskDocId) return;
    try {
      const response = await fetch(`${baseurl}/task/submission/view`, {
        method: "POST",
        body: JSON.stringify({ user_id: effectiveUserId, task_doc_id: taskDocId }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`,
        },
      });
      const data = await response.json();
      if (data.status === true && data.data) {
        setUserSubmissions(data.data.submissions || []);
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  }

  useEffect(() => {
    if (mainTopicID && effectiveUserId) {
      fetchMentorSubmissions();
    }
  }, [mainTopicID, effectiveUserId, categoryID]);

  async function commentAdd(activityid, contentid, commentText) {
    const formData = new FormData();
    formData.append("activity_id", activityid);
    formData.append("content_id", contentid);
    formData.append("content", commentText);
    formData.append("created_by_id", USERID);
    formData.append("created_by_name", UserName);
    formData.append("file", file);

    try {
      const response = await fetch(`${baseurl}/comment/add`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${key}` },
      });
      const data = await response.json();
      if (data.status === true) {
        activityGet();
        setActivityComments((prev) => ({ ...prev, [contentid]: "" }));
      }
    } catch (error) {
      console.error("Error while posting comment:", error);
    }
  }

  useEffect(() => {
    if (location.state || learningData?.singleTopic) {
      setAiID(location.state?.aiID !== undefined ? location.state.aiID : learningData?.aiID);
      setSingleTopic(location.state?.singleTopic || learningData?.singleTopic);
      setMappedLessons(location.state?.mapped !== undefined ? location.state.mapped : learningData?.mapped);
      setLearningID(location.state?.learning || learningData?.learning);
      setMainTopicID(location.state?.mainID !== undefined ? location.state.mainID : learningData?.mainID);
      setTopicNo(location.state?.topicNo !== undefined ? location.state.topicNo : learningData?.topicNo);
      setUniqueId(location.state?.uniqueId !== undefined ? location.state.uniqueId : learningData?.uniqueId);
      setRoomID(location.state?.roomID !== undefined ? location.state.roomID : learningData?.roomID);
      setAssessmentExist(location.state?.assessmentExist !== undefined ? location.state.assessmentExist : learningData?.assessmentExist);
      setAssessmentInfo(location.state?.assessmentInfo !== undefined ? location.state.assessmentInfo : learningData?.assessmentInfo);
      setCategoryID(
        location.state?.category_id !== undefined
          ? location.state.category_id
          : learningData?.category_id ?? null
      );
    }
  }, [location.state, learningData]);


  const handleNext = async () => {
    const currentTask = activityDetails[activeTaskIndex];

    // Check if clicking "Finish Line"
    if (activeTaskIndex === activityDetails.length - 1 && activeContentIndex === currentTask?.task_content?.length - 1) {
      handleFinish();
      return;
    }

    if (activeContentIndex < currentTask.task_content.length - 1) {
      setActiveContentIndex(activeContentIndex + 1);
    } else if (activeTaskIndex < activityDetails.length - 1) {
      setActiveTaskIndex(activeTaskIndex + 1);
      setActiveContentIndex(0);
    }
  };

  const handleFinish = async () => {
    setIsFinishing(true);
    try {
      const finishResp = await fetch(`${baseurl}/task/finish`, {
        method: "POST",
        body: JSON.stringify({
          user_id: effectiveUserId,
          org_id: OrgID,
          topic_id: singleTopic.id,
          main_topic_id: mainTopicID,
          learning_id: learningID,
          task_doc_id: taskDocId
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${key}`
        }
      });
      const finishData = await finishResp.json();

      if (finishData.status === true) {
        const summaryResp = await fetch(`${baseurl}/task/finish/summary`, {
          method: "POST",
          body: JSON.stringify({
            user_id: effectiveUserId,
            task_doc_id: taskDocId
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${key}`
          }
        });
        const summaryData = await summaryResp.json();
        const summary = summaryData.status === true ? summaryData.summary : "Congratulations on completing your tasks!";

        // 3. Navigate to completion page
        navigate("/task/completion", {
          state: {
            summary,
            courseName: singleTopic?.value
          }
        });
      } else {
        alert(finishData.description || "Failed to finish task. Please ensure all required fields are submitted.");
      }
    } catch (error) {
      console.error("Error during finish:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsFinishing(false);
    }
  };

  const checkFinishEnabled = () => {
    if (!activityDetails || activityDetails.length === 0) return false;
    for (let t_idx = 0; t_idx < activityDetails.length; t_idx++) {
      const task = activityDetails[t_idx];
      for (let c_idx = 0; c_idx < task.task_content?.length; c_idx++) {
        const content = task.task_content[c_idx];
        const fields = content.submission_fields || [];

        for (const field of fields) {
          if (field.required) {
            // Check if this specific field has an entry in userSubmissions
            const hasSub = userSubmissions.some(s =>
              s.task_index === t_idx &&
              s.content_index === c_idx &&
              (s.answers?.[field.id] || s.answers?.[String(field.id)])
            );
            if (!hasSub) return false;
          }
        }
      }
    }
    return true;
  };

  const isFinishEnabled = checkFinishEnabled();

  const handleInputChange = (contentIndex, fieldId, value) => {
    setSubmissionData(prev => ({
      ...prev,
      [contentIndex]: {
        ...(prev[contentIndex] || {}),
        [fieldId]: value
      }
    }));
  };

  const handleFileChange = (contentIndex, fieldId, file) => {
    setSubmissionData(prev => ({
      ...prev,
      [contentIndex]: {
        ...(prev[contentIndex] || {}),
        [fieldId]: file
      }
    }));
  };
  const submitMarks = async (task, key) => {
    try {
      const resp = await fetch(`${baseurl}/task/submission/evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: effectiveUserId,
          task_doc_id: task.task_doc_id,
          task_index: task.task_index,
          content_index: task.content_index,
          marks_obtained: enteredMarks[key]
        })
      });
      const data = await resp.json();
      if (data.status === true) {
        // Optimistically update marks in current topic view
        setSelectedTopic((prev) => {
          if (!prev) return prev;
          const updatedTasks = (prev.tasks || []).map((t) =>
            t.task_doc_id === task.task_doc_id &&
            t.task_index === task.task_index &&
            t.content_index === task.content_index
              ? {
                  ...t,
                  marks_obtained: Number(enteredMarks[key] || 0),
                  evaluated: true,
                }
              : t
          );
          return { ...prev, tasks: updatedTasks };
        });
        setMarksEditorOpenByKey((prev) => ({ ...prev, [key]: false }));
        setShowSnackbar(true);
        fetchMentorSubmissions();
        setTimeout(() => setShowSnackbar(false), 3000);
      } else {
        alert(data.description || "Failed to submit marks");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmission = async (contentIndex, submissionFields) => {
    const data = submissionData[contentIndex] || {};

    // Simple validation
    const missingFields = submissionFields.filter(f => f.required && !data[f.id]);
    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.map(f => f.label).join(", ")}`);
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("user_id", effectiveUserId);
    formData.append("org_id", OrgID);
    formData.append("topic_id", singleTopic.id);
    formData.append("task_doc_id", taskDocId);
    formData.append("task_index", activeTaskIndex);
    formData.append("content_index", contentIndex);

    Object.keys(data).forEach((key) => {
      formData.append(key, data[key]);
    });

    try {
      const response = await fetch(`${baseurl}/task/submit`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${key}`,
        },
      });
      const result = await response.json();
      if (result.status === true) {
        alert("Submission successful!");
        fetchSubmissions(); // Refresh submissions
        setSubmissionData((prev) => {
          const newState = { ...prev };
          delete newState[contentIndex];
          return newState;
        });
      } else {
        alert(`Submission failed: ${result.description}`);
      }
    } catch (error) {
      console.error("Submission failed:", error);
      alert("An error occurred during submission.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSubmissionFields = (fields, contentIndex) => {
    if (!fields || fields.length === 0) return null;

    const existingSubmission = userSubmissions.find(
      (s) => s.task_index === activeTaskIndex && s.content_index === contentIndex
    );

    return (
      <div className={style.submissionSection}>
        <h3 className={style.submissionTitle}>
          {existingSubmission ? "Your Submitted Response" : "Your Submission"}
        </h3>
        {fields.map((field) => {
          const submittedValue = existingSubmission?.answers?.[field.id];

          return (
            <div key={field.id} className={style.fieldGroup}>
              <label className={style.fieldLabel}>
                {field.label} {field.required && !existingSubmission && <span>*</span>}
              </label>

              {existingSubmission ? (
                <div className={style.submittedResult}>
                  {field.type === "textarea" ? (
                    <p className={style.resultText}>{submittedValue || "No response provided"}</p>
                  ) : field.type === "file" ? (
                    submittedValue ? (
                      <a
                        href={formatUrl(submittedValue)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.resultFileLink}
                      >
                        <FaFileAlt /> View Submitted File
                      </a>
                    ) : (
                      <p className={style.resultText}>No file uploaded</p>
                    )
                  ) : null}
                </div>
              ) : field.type === "textarea" ? (
                <textarea
                  className={style.textareaField}
                  placeholder="Enter your response here..."
                  value={submissionData[contentIndex]?.[field.id] || ""}
                  onChange={(e) => handleInputChange(contentIndex, field.id, e.target.value)}
                />
              ) : field.type === "file" ? (
                <div className={style.fileInputField}>
                  <input
                    type="file"
                    id={`file-${field.id}`}
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(contentIndex, field.id, e.target.files[0])}
                  />
                  <label htmlFor={`file-${field.id}`} className={style.fileInputLabel}>
                    <FaUpload />
                    <span>
                      {submissionData[contentIndex]?.[field.id]?.name ||
                        "Click to upload or drag and drop"}
                    </span>
                  </label>
                </div>
              ) : null}
            </div>
          );
        })}

        {!existingSubmission && (
          <div className={style.submitBtnContainer}>
            <button
              className={style.submitButton}
              onClick={() => handleSubmission(contentIndex, fields)}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Response"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderContent = (content) => {
    if (!content) return null;

    return (
      <div className={style.contentWrapper}>
        <h1 className={style.taskMainTitle}>{content.title}</h1>

        <div className={style.taskIntroduction}>
          {content.blocks ? (
            content.blocks.map((block, idx) => (
              <div key={idx} className={style.contentBlock}>
                {block.type === "text" ? (
                  <div dangerouslySetInnerHTML={{ __html: block.value }}></div>
                ) : block.type === "image" ? (
                  <div className={style.imageBlock}>
                    <img src={formatUrl(block.file || block.value)} alt="Content" />
                  </div>
                ) : block.type === "video" ? (
                  <div className={style.videoBlock}>
                    <video controls>
                      <source src={formatUrl(block.file || block.value)} type="video/mp4" />
                    </video>
                  </div>
                ) : block.type === "file" ? (
                  <a href={formatUrl(block.file || block.value)} className={style.fileBlock} target="_blank" rel="noopener noreferrer">
                    <FaFileAlt />
                    <span>Download Resource</span>
                  </a>
                ) : null}
              </div>
            ))
          ) : (
            <div>
              {content.type === "url" ? (
                <a href={formatUrl(content.text)} target="_blank" rel="noopener noreferrer">{content.text}</a>
              ) : content.type === "video" ? (
                <video controls width="100%" height="auto">
                  <source src={formatUrl(content.text)} type="video/mp4" />
                </video>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: content.text || content.content?.[0]?.content }}></div>
              )}
            </div>
          )}
        </div>

        {renderSubmissionFields(content.submission_fields, content.index)}
      </div>
    );
  };

  return (
    <div className={style.container}>
      {(!isMobile || !showTaskContent) && (
        <div className={style.header}>
          <button
            type="button"
            className={style.backButton}
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <IoChevronBack />
          </button>
          <div className={style.headerTextGroup}>
            <span className={style.headerKicker}>Tasks</span>
            <h1 className={style.headerTitle}>Student submissions</h1>
          </div>
        </div>
      )}

      <div className={style.pageLayout}>
        <div className={`${style.sidebar} ${isMobile && showTaskContent ? style.hidden : ""}`}>
          <div className={style.taskList}>
            {userSubmissions?.filter(Boolean).map((main, i) => (
              <div key={i} className={style.mainTopicItem}>

                {/* MAIN TOPIC */}
                <div
                  className={style.mainTopicHeader}
                  onClick={() => toggleMainTopic(main?.main_topic_id)}
                >
                  <div className={style.taskMarker}>📘</div>
                  <div className={style.taskInfo}>
                    <p className={style.taskTitleMain}>
                      {main.main_topic_name}
                    </p>
                  </div>
                  <span>
                    {expandedMainTopics[main.main_topic_id] ? "▲" : "▼"}
                  </span>
                </div>

                {expandedMainTopics[main.main_topic_id] && (
                  <div className={style.topicDropdown}>
                    {main?.topics?.map((topic, j) => (
                      <div
                        key={j}
                        className={`${style.topicItem} ${selectedTopic?.topic_id === topic.topic_id ? style.activeTopic : ""
                          }`}
                        onClick={() => {
                          setSelectedTopic(topic);
                          if (isMobile) setShowTaskContent(true);
                        }}
                      >
                        <div className={style.taskMarker}>•</div>
                        <div className={style.taskInfo}>
                          <p className={style.taskTitleMain}>
                            {topic.topic_name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className={style.taskItem}>
              <div className={style.taskMarker}>⚐</div>
              {/* <div className={style.taskInfo}>
                <p className={style.taskTitleMain}>Finish</p>
              </div> */}
            </div>
          </div>
        </div>

        <div className={`${style.contentArea} ${isMobile && !showTaskContent ? style.hidden : ""}`}>
          <div className={style.rightTopHeader}>
            {isMobile && (
              <button className={style.mobileBackButton} onClick={() => setShowTaskContent(false)}>
                <IoChevronBack />  <span>Task {activeTaskIndex + 1}: {activityDetails[activeTaskIndex]?.task_title}</span>
              </button>
            )}

            {selectedTopic && mentorTaskGroups.length > 1 ? (
              <div className={style.mentorTaskPaginationRow}>
                <span className={style.mentorTaskPaginationMeta}>
                  Task {mentorTaskPageIndex + 1} of {mentorTaskGroups.length}
                </span>
                <div className={style.paginationBullets} role="tablist" aria-label="Submitted tasks">
                  {mentorTaskGroups.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      role="tab"
                      aria-selected={idx === mentorTaskPageIndex}
                      className={`${style.bullet} ${idx === mentorTaskPageIndex ? style.activeBullet : ""}`}
                      onClick={() => setMentorTaskPageIndex(idx)}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={style.paginationBullets}>
                {activityDetails[activeTaskIndex]?.task_content?.map((_, idx) => (
                  <div
                    key={idx}
                    className={`${style.bullet} ${activeContentIndex === idx ? style.activeBullet : ""}`}
                    onClick={() => setActiveContentIndex(idx)}
                  >
                    {idx + 1}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={style.ActivityBody}>
            {mentorLoading ? (
              <Loader />
            ) : 
              selectedTopic?(
              <div className={style.mentorViewContainer} ref={mentorViewTopRef}>
                <div className={style.topicPageHeader}>
                  <p className={style.topicPageLabel}>Reviewing</p>
                  <h2 className={style.topicPageTitle}>{selectedTopic.topic_name}</h2>
                </div>

                {mentorTaskGroups[mentorTaskPageIndex] ? (
                  <div className={style.taskGroup}>
                    <h3 className={style.groupTaskTitle}>
                      Task: {mentorTaskGroups[mentorTaskPageIndex][0].task_title}
                    </h3>

                    {mentorTaskGroups[mentorTaskPageIndex].map((task, i) => {
                        const markKey = `${task.task_doc_id}-${task.task_index}-${task.content_index}`;
                        const evaluated = isSubmissionEvaluated(task);
                        const marksFormOpen =
                          marksEditorOpenByKey[markKey] === true;
                        return (
                        <div key={i} className={style.mentorCard}>
                          <div className={style.mentorCardTop}>
                            <h3 className={style.contentHead}>{task.content_title}</h3>
                            <div className={style.scorePill} title="Current score">
                              <span className={style.scorePillValue}>
                                {task.marks_obtained ?? 0}
                              </span>
                              <span className={style.scorePillDivider}>/</span>
                              <span className={style.scorePillMax}>{task.total_mark}</span>
                            </div>
                          </div>

                          {task.answers.map((ans, j) => {
                            const href = getSubmissionAnswerHref(ans.answer);
                            const showFileReview =
                              submissionAnswerIsReviewableFileUrl(ans.answer);
                            const showImagePreview =
                              showFileReview && submissionAnswerIsImageUrl(ans.answer);
                            return (
                              <div key={j} className={style.answerBlock}>
                                <p className={style.answerQuestion}>
                                  <span className={style.answerQBadge}>Q</span>
                                  <span>{ans.question}</span>
                                </p>
                                <div className={style.answerResponse}>
                                  <span className={style.answerABadge}>A</span>
                                  {showFileReview ? (
                                    <div className={style.fileAnswerWrap}>
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={style.fileReviewLink}
                                      >
                                        <FaFileAlt aria-hidden />
                                        <span>
                                          Review file —{" "}
                                          {displayNameFromSubmissionUrl(ans.answer)}
                                        </span>
                                      </a>
                                      {/* <span className={style.fileAnswerHint}>
                                        Opens in a new tab
                                      </span> */}
                                      {showImagePreview && (
                                        <a
                                          href={href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={style.filePreviewLink}
                                        >
                                          <img
                                            src={href}
                                            alt="Submission preview"
                                            className={style.filePreviewThumb}
                                          />
                                        </a>
                                      )}
                                    </div>
                                  ) : (
                                    <p className={style.answerPlain}>{ans.answer}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {task.submitted_at && (
                            <p className={style.submissionTime}>
                              <span className={style.submissionTimeIcon} aria-hidden>
                                ●
                              </span>
                              Submitted {task.submitted_at}
                            </p>
                          )}
                          {marksFormOpen && (
                            <div className={style.marksSection}>
                              <p className={style.marksSectionLabel}>
                                <FaPencilAlt
                                  aria-hidden
                                  className={style.marksSectionLabelIcon}
                                />
                                {evaluated ? "Update mark" : "Add mark"}
                              </p>
                              <div className={style.marksControls}>
                                <input
                                  type="number"
                                  placeholder="Marks"
                                  min={0}
                                  step="any"
                                  aria-label="Enter marks for this submission"
                                  value={
                                    enteredMarks[markKey] !== undefined
                                      ? enteredMarks[markKey]
                                      : task.marks_obtained != null
                                        ? String(task.marks_obtained)
                                        : ""
                                  }
                                  onChange={(e) => handleMarksChange(markKey, e.target.value)}
                                />
                                <button
                                  type="button"
                                  className={style.marksSubmitBtn}
                                  onClick={() => submitMarks(task, markKey)}
                                >
                                  Save marks
                                </button>
                                <button
                                  type="button"
                                  className={style.marksCancelBtn}
                                  onClick={() =>
                                    setMarksEditorOpenByKey((prev) => ({
                                      ...prev,
                                      [markKey]: false,
                                    }))
                                  }
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                          {!marksFormOpen && (
                            <button
                              type="button"
                              className={style.marksRevealBtn}
                              onClick={() =>
                                setMarksEditorOpenByKey((prev) => ({
                                  ...prev,
                                  [markKey]: true,
                                }))
                              }
                            >
                              <FaPencilAlt aria-hidden className={style.marksRevealIcon} />
                              <span>{evaluated ? "Update mark" : "Add mark"}</span>
                            </button>
                          )}
                        </div>
                      );
                      })}
                  </div>
                ) : null}

                {mentorTaskGroups.length > 1 && (
                  <div className={style.mentorTaskFooterNav}>
                    <button
                      type="button"
                      className={style.mentorTaskNavBtn}
                      disabled={mentorTaskPageIndex <= 0}
                      onClick={() =>
                        setMentorTaskPageIndex((p) => Math.max(0, p - 1))
                      }
                    >
                      Previous
                    </button>
                    <span className={style.mentorTaskFooterMeta}>
                      {mentorTaskPageIndex + 1} / {mentorTaskGroups.length}
                    </span>
                    <button
                      type="button"
                      className={style.mentorTaskNavBtnPrimary}
                      disabled={
                        mentorTaskPageIndex >= mentorTaskGroups.length - 1
                      }
                      onClick={() =>
                        setMentorTaskPageIndex((p) =>
                          Math.min(mentorTaskGroups.length - 1, p + 1)
                        )
                      }
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
          ) : (
          <div className={style.emptySubmissions} role="status">
            <p className={style.emptySubmissionsTitle}>No submissions yet</p>
            <p className={style.emptySubmissionsText}>
              When the learner submits work for this topic, it will appear here for review.
            </p>
          </div>
            )}
        </div>

      </div>
    </div>
    {showSnackbar && (
  <div className={style.snackbar}>
    Marks submitted successfully!
  </div>
)}
    </div >
  );
}

export default TaskMentorView;
